import { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';

const ScreenshotErrorsTable = () => {
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [investigatingError, setInvestigatingError] = useState(null);
  const [investigationData, setInvestigationData] = useState(null);
  const [isInvestigating, setIsInvestigating] = useState(false);

  const [proxies, setProxies] = useState({});
  const [websites, setWebsites] = useState({});

  // API configuration
  const apiKey = process.env.REACT_APP_API_KEY;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch errors, proxies and websites when component mounts
  useEffect(() => {
    const fetchErrorsAndProxys = async () => {
      try {
        // Fetch errors
        const errorsResponse = await axios.get(`${apiUrl}/websites/errors`, {
          headers: { 'x-api-key': apiKey },
        });
        // Log the fetched errors to verify their structure
        console.log('Fetched Errors:', errorsResponse.data.errors);
        
        // Fetch proxies
        const proxyResponse = await axios.get(`${apiUrl}/proxys`, {
          headers: { 'x-api-key': apiKey },
        });
        const proxyMap = {};
        if (proxyResponse.data && Array.isArray(proxyResponse.data)) {
          proxyResponse.data.forEach(p => {
            proxyMap[p.id] = p.name;
          });
        }

        // Fetch websites configurations
        const websitesResponse = await axios.get(`${apiUrl}/websites`, {
          headers: { 'x-api-key': apiKey },
        });
        const websiteMap = {};
        if (websitesResponse.data && Array.isArray(websitesResponse.data)) {
          websitesResponse.data.forEach(w => {
            websiteMap[w.WebsitesDBID] = w;
          });
        }

        setProxies(proxyMap);
        setWebsites(websiteMap);
        setErrors(errorsResponse.data.errors);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchErrorsAndProxys();
  }, [apiKey, apiUrl]);

  // Helper function to group errors by date
  const groupErrorsByDate = () => {
    const groupedErrors = {};

    errors.forEach((error) => {
      const date = error.Timestamp.split('T')[0];
      if (!groupedErrors[date]) {
        groupedErrors[date] = [];
      }
      groupedErrors[date].push(error);
    });

    return groupedErrors;
  };

  const handleInvestigate = async (error) => {
    setInvestigatingError(error);
    setInvestigationData(null);
    setIsInvestigating(true);
    setDetailsModalOpen(true);

    try {
      const websitesDbId = error.WebsitesDBID?.S || error.WebsitesDBID;
      const timestamp = error.Timestamp?.S || error.Timestamp;
      const failbackAttempt = error.FailbackAttempt?.N || error.FailbackAttempt || '0';

      const response = await axios.get(`${apiUrl}/websites/error-details`, {
        headers: { 'x-api-key': apiKey },
        params: {
          WebsitesDBID: websitesDbId,
          Timestamp: timestamp,
          FailbackAttempt: failbackAttempt
        }
      });
      setInvestigationData(response.data);
    } catch (err) {
      console.error('Error fetching details:', err);
      setInvestigationData({ trueError: 'Could not fetch details. The execution history may have expired.' });
    } finally {
      setIsInvestigating(false);
    }
  };

  const handleCloseModal = () => {
    setDetailsModalOpen(false);
  };

  const groupedErrors = groupErrorsByDate();

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Screenshot Errors
      </Typography>

      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : (
        Object.keys(groupedErrors).map((date) => (
          <Box key={date} mb={4}>
            <Typography variant="h6" gutterBottom>
              {new Date(date).toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>

            <Table>
            <TableHead>
              <TableRow>
                <TableCell>Website</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Error Message</TableCell>
                <TableCell>Failback Attempt</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedErrors[date].map((error) => {
                const failbackAttempt = error.FailbackAttempt?.N || error.FailbackAttempt || '0';
                const errorMessage = error.ErrorMessage?.S || error.ErrorDetails || 'No message';
                const websiteName = error.WebsiteName?.S || 'Unknown';

                return (
                  <TableRow key={error.id || `${websiteName}-${error.Timestamp}`}>
                    <TableCell>{websiteName}</TableCell>
                    <TableCell>
                      <a href={error.URL?.S || error.URL} target="_blank" rel="noopener noreferrer">
                        {error.URL?.S || error.URL}
                      </a>
                    </TableCell>
                    <TableCell>{errorMessage}</TableCell>
                    <TableCell>Attempt {failbackAttempt}</TableCell>
                    <TableCell>{new Date(error.Timestamp?.S || error.Timestamp).toLocaleString('en-GB')}</TableCell>
                    <TableCell>
                      <Button variant="outlined" size="small" onClick={() => handleInvestigate(error)}>
                        Investigate
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            </Table>
          </Box>
        ))
      )}

      <Dialog open={detailsModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Error Details</DialogTitle>
        <DialogContent dividers>
          {isInvestigating ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : investigationData ? (
            <Box>
              {investigationData.attempts && investigationData.attempts.length > 0 ? (
                investigationData.attempts.map((attempt, index) => {
                  const website = websites[investigatingError?.WebsitesDBID?.S || investigatingError?.WebsitesDBID];
                  const failbackOrder = website?.ScreenshotFailbackOrder || [];
                  const matchingOrderEntry = failbackOrder[attempt.attempt];
                  const configString = matchingOrderEntry?.Config;
                  let parsedConfig = null;
                  if (configString) {
                    try {
                      parsedConfig = typeof configString === 'string' ? JSON.parse(configString) : configString;
                    } catch (e) {
                      parsedConfig = { raw: configString };
                    }
                  }

                  return (
                    <Box key={index} mb={3} p={2} border={1} borderColor="grey.300" borderRadius={2}>
                      <Typography variant="h6" gutterBottom color="primary">Attempt {attempt.attempt}</Typography>
                      
                      <Typography variant="subtitle2" color="textSecondary">Provider</Typography>
                      <Typography paragraph>{attempt.provider || 'N/A'}</Typography>
                      
                      <Typography variant="subtitle2" color="textSecondary">Proxy</Typography>
                      <Typography paragraph>{proxies[attempt.proxy] || attempt.proxy || 'N/A'}</Typography>
                      
                      <Typography variant="subtitle2" color="textSecondary">Parameters</Typography>
                      {parsedConfig && Object.keys(parsedConfig).length > 0 ? (
                        <Typography variant="body2" component="pre" sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 2, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {JSON.stringify(parsedConfig, null, 2)}
                        </Typography>
                      ) : (
                        <Typography paragraph>None</Typography>
                      )}
                    
                    <Typography variant="subtitle2" color="textSecondary">True Error Message</Typography>
                    <Typography variant="body2" component="pre" sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {attempt.error || 'N/A'}
                    </Typography>
                  </Box>
                  );
                })
              ) : (
                <Typography color="error">No attempt details found in execution history. History may have expired or format changed.</Typography>
              )}

              {investigationData.executionName && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary">Execution Name</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{investigationData.executionName}</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScreenshotErrorsTable;
